<template>
<v-dialog
    v-model="dialogModel"
    :fullscreen="isFullScreen"
    :max-width="isFullScreen ? '' : 700"
    persistent
    transition="dialog-bottom-transition">
  <v-card v-if="!!dialogModel">
    <v-toolbar dark color="primary">
      <v-btn icon dark @click="dialogModel = false">
        <v-icon>
          close
        </v-icon>
      </v-btn>

      <v-toolbar-title>
        {{ $vuetify.t('$vuetify.account.addPaymentMethod') }}
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <v-toolbar-items>
        <v-btn dark flat @click="save">
          {{ $vuetify.t('$vuetify.common.save') }}
        </v-btn>
      </v-toolbar-items>
    </v-toolbar>

    <v-card-text id="payment-form">
      <v-text-field
        v-model="model.name"
        prepend-icon="person"
        name="name_on_card"
        v-validate="'required'"
        :label="$vuetify.t('$vuetify.account.nameOnCard')"
        required
        :error-messages="fieldErrors.name"
        @input="fieldErrors.name = null"
      ></v-text-field>

      <template v-if="isStripeStore">
        <div class="custom-stripe-element">
          <div id="icon">
            <v-icon>
              credit_card
            </v-icon>
          </div>

          <div id="content" style="width: calc(100% - 32px)">
            <label> {{ $vuetify.t('$vuetify.account.cardNumber') }} </label>
            <div id="card-number">
            </div>
            <hr>
          </div>
        </div>

        <div class="custom-stripe-element">
          <div id="icon">
            <v-icon>
              event
            </v-icon>
          </div>

          <div id="content" style="width: calc(50% - 34px); padding-right: 10px">
            <label> Expirey Date </label>
            <div id="card-expiry">
            </div>
            <hr>
          </div>

          <div id="content" style="width: calc(50%); margin-left: 5px">
            <label> {{ $vuetify.t('$vuetify.account.cvc') }} </label>
            <div id="card-cvc">
            </div>
            <hr>
          </div>
        </div>
      </template>
      <v-layout row wrap v-else>
        <v-flex xs12>
          <v-text-field
            v-model="model.no"
            name="card_number"
            prepend-icon="credit_card"
            v-validate="'required'"
            :label="$vuetify.t('$vuetify.account.cardNumber')"
            mask="credit-card"
            required
            :error-messages="fieldErrors.no"
            @input="fieldErrors.no = null"
          ></v-text-field>
        </v-flex>

        <v-flex xs7 md6>
          <v-text-field
            v-model="model.expired"
            name="card_exp"
            prepend-icon="event"
            v-validate="'required|digits:4'"
            :label="$vuetify.t('$vuetify.account.mmyy')"
            mask="## / ##"
            class="pr-3"
            required
            :error-messages="fieldErrors.expiredMonth"
            @input="fieldErrors.expiredMonth = null"
          ></v-text-field>
        </v-flex>

        <v-flex xs5 md6>
          <v-text-field
            v-model="model.csv"
            name="card_cvc"
            :label="$vuetify.t('$vuetify.account.cvc')"
            mask="####"
            required
            :error-messages="fieldErrors.csv"
            @input="fieldErrors.csv = null"
          ></v-text-field>
        </v-flex>
      </v-layout>

      <v-layout row wrap>
        <v-flex xs6>
          <v-autocomplete
            v-model="selectedCountry"
            prepend-icon="place"
            :items="countries"
            item-text="country"
            required
            :error-messages="fieldErrors.country"
            :label="$vuetify.t('$vuetify.account.country')"
            return-object
            :rules="[() => !!selectedCountry || 'This field is required']"
            @change="handleCountryChange"

            class="pr-3"
          ></v-autocomplete>
        </v-flex>

        <v-flex xs6>
          <v-text-field
            v-model="model.postalCode"
            name="card_postalCode"
            v-mask='parseMask(selectedCountry.format)'
            :label="$vuetify.lang.current === 'zhHans' ? '邮编' : (selectedCountry.name || 'Postal Code')"
            required
            :error-messages="fieldErrors.postalCode"
            @input="fieldErrors.postalCode = null"
          ></v-text-field>

          <div v-if="!selectedCountry.format" style="font-size: 12px; text-align: left;">
            {{ $vuetify.t('$vuetify.account.otherPostalCode') }}
          </div>
        </v-flex>
      </v-layout>
    </v-card-text>

    <div v-if="loading" id="loadingContainer">
      <v-progress-circular
        :size="70"
        :width="7"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
  </v-card>
</v-dialog>
</template>

<script>
  import { countries } from './countries.js'
  import get from 'lodash/get';

  import { loadStripe, confirmCardSetup } from '@stripe/stripe-js';
  import { mapGetters, mapActions } from 'vuex'

  export default {
    data() {
      return {
        source: null,
        loading: false,

        stripe:  null,
        stripeCard: null,
        elements: null,


        model: {
          name: null,
          no: null,
          expired: null,
          expiredMonth: null,
          expiredYear: null,
          csv: null,

          country: null,
          postalCode: null
        },

        selectedCountry: {},
        countries: countries,
        fieldErrors: {}
      }
    },

    computed: {
      dialogModel: {
        get() {
          return this.getAddPaymentMethodDialog
        },
        set(val) {
          this.setAddPaymentMethodDialog(!!val)
        }
      },

      isFullScreen() {
        return this.$vuetify.breakpoint.smAndDown || this.$route.name === 'store-account'
      },

      isStripeStore() {
        return get(this.getStore, 'settings.paymentProvider') == 'STRIPE'
      },

      ...mapGetters([ 'getAddPaymentMethodDialog', 'getStore' ])
    },

    methods: {
      parseMask(format) {
        if (!!format)
          return format.split(' or ')
        return 'XXXXXXXXXXXXXXXXXXXX'
      },

      async save() {
        let _this = this
        this.loading = true

        const postalCode = (this.model.postalCode || '').replace(/-/g, '').replace(/ /g, '')
        const country = this.selectedCountry.code

        if (this.isStripeStore) {
          const { data } = await this.$api.get('/customer/payment_method/setup_intent')
          const { secret } = data

          this.stripe.confirmCardSetup(secret, {
            payment_method: {
              card: this.cardNumberElement,
              billing_details: {
                name: this.model.name,
                address: {
                  country: country,
                  postal_code: postalCode
                }
              }
            }
          }).then(function(result) {
            if (result.setupIntent) {
              _this.$bus.$emit('PaymentListFetched')
              _this.fetchPaymentMethods()
              _this.dialogModel = false
            } else {
              _this.showSnack({
                text: get(result, 'error.message', null),
                color: 'error'
              })
            }

            this.loading = false
          }).catch(() => {
            this.loading = false
          })
        } else {
          var error = false
          const { no, name, expired = '0000', csv } = this.model

          const expiredMonth = expired.substring(0, 2);
          const expiredYear = expired.substring(2, 5);

          const payload = {
            name,
            no,
            expiredMonth,
            expiredYear,
            csv,
            postalCode: postalCode,
            country: country
          }

          for (let field in payload) {
            // All fields are required but postalcode which is conditional
            if (!payload[field]) {
              this.$set(this.fieldErrors, field, `${field} is required`)
              error = true
              this.$forceUpdate()
            }
          }

          // Only submit the request if no errors were found
          if (!error) {
            this.addPaymentMethod(payload).then(() => {
              this.$bus.$emit('PaymentListFetched')
              this.loading = false
            }).catch(() => {
              this.loading = false
            })
          } else {
            this.loading = false
          }
        }
      },

       async mountStripe() {
        this.stripe = await loadStripe(process.env.VUE_APP_STRIPE, { locale: 'en' });

        const style = {
            base: {
                'fontSize': '16px',
                'color': 'rgba(0,0,0,.87)',
                '::placeholder': {
                  color: 'rgba(0,0,0,.35)'
                }
            }
        };

        this.elements = this.stripe.elements();
        this.cardNumberElement = this.elements.create('cardNumber', { style });
        this.cardNumberElement.mount('#card-number');

        this.cardExpiryElement = this.elements.create('cardExpiry', { style });
        this.cardExpiryElement.mount('#card-expiry');

        this.cardCvcElement = this.elements.create('cardCvc', {
          'placeholder': '123',
          style: style
        });
        this.cardCvcElement.mount('#card-cvc');
      },

      handleCountryChange() {
        this.model.postalCode = null
        this.fieldErrors.country = null
        this.fieldErrors.postalCode = null
      },

      ...mapActions([
        'setAddPaymentMethodDialog',
        'addPaymentMethod',
        'fetchPaymentMethods',
        'showSnack'
      ])
    },

    watch: {
      dialogModel() {
        this.fieldErrors = {}

        // Clear the form whenever the dialog closes
        if (!this.dialogModel) {
          this.loading = false

          for (let field in this.model)
            this.model[field] = ''
        } else {
          this.loading = false

          // Default the selected store to the store country
          const storeCountry = get(this.getStore, 'country', 'CA')
          this.selectedCountry = this.countries.find(country => country.code == storeCountry) || {}

          if (this.isStripeStore)
            this.mountStripe()
        }
      }
    }
  }
</script>
 <style scoped>
   .v-dialog--fullscreen .v-card__text {
     max-width: 700px;
     margin: auto;
     padding: 10px !important;
   }
   .v-card__text {
      padding: 20px 40px;
   }

  .custom-stripe-element {
    align-items: flex-start;
    display: flex;
    flex: 1 1 auto;
    text-align: left;
  }
  .custom-stripe-element>#icon{
    width: 32px !important;
    position: relative;
    top: 20px;
  }
  .custom-stripe-element>#content {
    position: relative;
    display: inline;
    /* width: calc(100% - 32px); */
    /* margin-left: 34px; */
  }
  hr {
    margin: 5px 0px 20px;
    border: 0 none;
    height: 1px;
    background-color: rgb(118, 118, 118);
  }
  #loadingContainer {
    position: fixed;
    top: 0px;
    height: 100vh;
    left: 0px;
    width: 100vw;

    background-color: rgba(240, 240, 240, 0.75);
  }
  #loadingContainer>div {
    position: absolute;
    top: calc(50% - 50px);
    left: calc(50% - 50px);
  }
</style>
<style>
  .custom-stripe-element label {
    float: left;
    text-align: left;

    color: rgba(0,0,0,.54);
    height: 20px;
    line-height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    top: 6px;
    transform-origin: top left;
    white-space: nowrap;
    pointer-events: none;
    font-size: 12px;
  }
  .ElementsApp input  {
    color: rgba(0,0,0,.87);
    font-size: 16px !important;
    line-height: 20px;
    padding: 8px 0;
    border-color: black;
    border-bottom-width: 2px;
  }
</style>
